<template lang="html">
  <div class="page-site-settings tier tier-two">
    <Tooltip content="Navigate through your website settings using this menu"/>

    <ul class="tier-list tier-content">
      <Permissions
        tag="li"
        class="tier-list-item"
        v-for="(item, index) in items"
        :key="`item-${index}`"
        :permissions="currentUser.permissions"
        :requireAll="item.permission">

        <router-link :to="item.link">
          <div>
            <Sprite :name="item.slug"/>
          </div>
          <div>
            <span>{{item.name}}</span>
            <p class="tier-description">{{item.description}}</p>
          </div>
        </router-link>
      </Permissions>
    </ul>

    <transition name="column-left">
      <router-view
        :key="routerKey"
        @updatePage="updatePage"/>
    </transition>
  </div>
</template>

<script>
import {mapState} from 'vuex'

import Sprite from '@/components/Sprite'
import Tooltip from '@/components/Tooltip'
import Permissions from '@/components/Permissions'

export default {
  name: 'SiteSettingsList',

  props: {
    companyId: String,
    siteId: String
  },

  computed: {
    ...mapState(['currentUser']),

    routerKey() {
      return this.$route.fullPath
    },

    items() {
      let items = [
        {
          name: 'General Settings',
          description: 'Configure the name and SEO settings of your site',
          slug: 'seo',
          link: {
            name: 'general-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Domain Name',
          description: 'Connect or register your domain name',
          slug: 'domain',
          link: {
            name: 'domain-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Logo',
          description: 'Upload your logo or have one designed',
          slug: 'logo',
          link: {
            name: 'logo-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Lock Screen',
          description: 'Control public access to your website',
          slug: 'site-visibility',
          link: {
            name: 'visibility-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Favicon',
          description: 'Add your own icon to the browser bar',
          slug: 'favicon',
          link: {
            name: 'favicon-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Google Analytics',
          description: 'Track your website visitors',
          slug: 'google-analytics',
          link: {
            name: 'analytics-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Google Tag Manager',
          description: 'GTM simplifies the process of working with tags',
          slug: 'advanced',
          link: {
            name: 'gtm-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'Facebook Pixel',
          description: 'Retarget website visitors on Facebook',
          slug: 'advanced',
          link: {
            name: 'facebook-pixel-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        },
        {
          name: 'External Scripts',
          description: 'Add some custom Javascript to the website',
          slug: 'advanced',
          link: {
            name: 'custom-js-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          },
          permission: {
            company: ['create']
          }
        },
        {
          name: 'Delete My Website',
          description: 'Permanently delete your website and content',
          slug: 'bin',
          link: {
            name: 'delete-site',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          },
          permission: {
            site: ['delete']
          }
        }
      ]

      if (this.$store.state.whiteLabel) {
        items.push({
          name: 'Shop Integration',
          description: 'Integrate Shopify to sell products & services',
          slug: 'shopify',
          link: {
            name: 'shopify-settings',
            params: {
              companyId: this.companyId,
              siteId: this.siteId
            }
          }
        })
      }

      return items
    }
  },

  components: {
    Sprite,
    Tooltip,
    Permissions
  },

  methods: {
    updatePage(notification) {
      this.$emit('updatePage', notification)
    }
  }
}
</script>

<style lang="sass">
  .page-site-settings
    box-shadow: inset -30px 0px 30px 0px rgba(0, 0, 0, 0.05)
</style>
